import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { useFlowErrorDetector, useFormValidationWithState } from '@makemydeal/dr-shared-ui-utils';
import { customerValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/customerValidationConfig';
import { POSTAL_CODE_DIGITS_ONLY_TOGGLE } from '@makemydeal/dr-shared-ui-utils/validationConfigs/addressValidationConfig';
import { resolveCellPhone } from '@makemydeal/dr-shared-ui-utils/phone/resolvePhone';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { CustomerFormContext } from './CustomerFormContext';
import PersonalInfoFields from './sections/PersonalInfoFields';
import PhoneNumberFields from './sections/PhoneNumberFields';
import EmailFields from './sections/EmailFields';
import CustomerAddresses from './sections/addresses/CustomerAddresses';
import CustomerSpd from './sections/spd/CustomerSpd';
import { getSpdCheckError } from '../store/customerSpdSelectors';
import { hasSpdChanged, mergeNonEmptyFields } from './sections/spd/spd.utils';
import HousingAndEmploymentInfo from './sections/housingAndEmployment/HousingAndEmploymentInfo';
import { hasAnyFieldValue } from './sections/housingAndEmployment/housingAndEmployment.utils';
import BusinessNameField from './sections/business/BusinessNameField';
import BusinessInformation from './sections/business/BusinessInformation';
import CreditConsentSection from './sections/consent/CreditConsentSection';
import { BUSINESS, transformSuffix } from '../constants';
import type {
    CardOperationKind,
    CustomerCardCapabilities,
    CustomerFormRef,
    CustomerFormValues,
    CustomerFormViewMode,
    CustomerTabKey,
    PhoneFieldKey
} from '../types';
import type { CoBuyer, IShopper } from '@makemydeal/dr-platform-types';
import type { OnAnalyticsEvent } from '../types';
import type { CustomerSpdValues } from '../types/customerSpd.types';
import { transformBirthDate } from '../utils/transformBirthDate.utils';

interface CustomerFormProps {
    customerRole: CustomerTabKey;
    initialValues?: IShopper | CoBuyer | null;
    capabilities?: CustomerCardCapabilities;
    /** Controls which fields are visible in section sub-components. Defaults to 'full'. */
    viewMode?: CustomerFormViewMode;
    /**
     * Called after any field value changes, once formValues has updated. Receives current dirty and valid state,
     * plus the latest primary email value (used by UnifiedCustomerCard for the cross-form duplicate-email check).
     * Also includes `hasContractErrors` when `capabilities.contractValidation` is true.
     */
    onChange?: (isDirty: boolean, isValid: boolean, email?: string, hasContractErrors?: boolean) => void;
    /**
     * The primary buyer's customerType value (e.g. 'Business' or 'Individual').
     * Only needed when rendering the co-buyer form — used to decide whether to show
     * Co-Applicant Type (buyer is Business) or Relationship to Buyer (buyer is not Business).
     */
    buyerCustomerType?: string;
    /**
     * The primary email address of the *other* customer (buyer when rendering co-buyer, co-buyer when
     * rendering buyer). Required by the validation engine to enforce the duplicate-email rule.
     */
    otherEmail?: string;
    /**
     * True while this (buyer) form is converting an individual to a business. A conversion has no
     * field-level diff of its own but is itself a pending change, so it reads as dirty.
     */
    isBusinessConversionFlow?: boolean;
    /**
     * True when this form is showing an operation's draft rather than a saved participant. The record
     * may be incomplete, so validation runs up front instead of waiting for the dealer to touch fields.
     */
    isOperationDraft?: boolean;
    isAnyOperationInProgress?: boolean;
    /**
     * True when the draft came from a chosen search result. Such a record can arrive incomplete, so its
     * gaps are surfaced immediately — unlike a record being created, where the form starts blank and
     * there is nothing for the dealer to fix yet.
     */
    isDraftFromSearchResult?: boolean;
    /**
     * Host analytics handler, forwarded onto context so leaf sections (e.g. CustomerSpd) can emit
     * neutral event names without prop-drilling. The activity never dispatches host actions itself.
     */
    onAnalyticsEvent?: OnAnalyticsEvent;
    /**
     * Optional opt-in render-slot forwarded onto context so PersonalInfoFields can render it beside
     * the "Personal Information" heading (buyer only). Undefined for hosts that don't use it.
     */
    personalInfoHeaderAction?: ReactNode;
    /**
     * Forwarded onto context so entry CTAs (e.g. Change Buyer) ask the card to start the operation,
     * letting it warn about unsaved edits first. Omitted when no card is hosting the form.
     */
    onRequestOperation?: (kind: CardOperationKind) => void;
    /** Forwarded onto context so CustomerSpd can include it on the SCI:SPDCheck NR event. */
    hideCheckLoader?: boolean;
}

// Phone values may arrive in different formats (e.g. '555-123-4567' vs '5551234567').
// Normalise to digits-only before dirty comparison to avoid false positives (ADR-5).
const normalizePhone = (v?: unknown) => String(v ?? '').replace(/\D/g, '');

const normalizeForDirtyCheck = (values: Record<string, unknown>) => {
    const normalized: Record<string, unknown> = {
        ...values,
        phone: normalizePhone(values.phone),
        cellPhone: normalizePhone(values.cellPhone),
        homePhone: normalizePhone(values.homePhone),
        workPhone: normalizePhone(values.workPhone)
    };
    // Dropped rather than coerced to `false`, so it matches an untouched baseline where the key is absent.
    if (!normalized.creditPullConsentGiven) delete normalized.creditPullConsentGiven;
    return normalized;
};

const CustomerForm = forwardRef<CustomerFormRef, CustomerFormProps>(
    (
        {
            customerRole,
            initialValues,
            capabilities = {},
            viewMode = 'full',
            onChange,
            buyerCustomerType,
            otherEmail,
            onAnalyticsEvent,
            isBusinessConversionFlow = false,
            isOperationDraft = false,
            isAnyOperationInProgress = false,
            isDraftFromSearchResult = false,
            personalInfoHeaderAction,
            onRequestOperation,
            hideCheckLoader
        },
        ref
    ) => {
        const isOptionalDeskingActive = capabilities.optionalDesking ?? false;
        const isLocked = capabilities.readOnly ?? false;
        const isBuyerBusiness = buyerCustomerType === BUSINESS;
        // Business flag — drives the individual↔business field switching
        // (title-as-text, hidden Personal Information heading / Marital Status / Housing & Employment).
        const isCurrentCustomerBusiness = initialValues?.customerType === BUSINESS;
        // The buyer-only business sections (Business Name / Signer / Business Information accordion)
        // render in the business buyer context. Capability-flag gating is handled by the host / the
        // sibling "Convert to Business Migration" story.
        const isBusinessBuyer = isCurrentCustomerBusiness && customerRole === 'buyer';
        const businessDetails = (initialValues as { business?: Record<string, unknown> } | null | undefined)?.business;
        const hasBusinessData = hasAnyFieldValue(...Object.values(businessDetails ?? {}));
        const creditAppFieldsCoverageToggle = useSelector(featureToggleSelectors.isCreditAppFieldsCoverageEnabled);
        const isCreditAppFieldsCoverageEnabled = capabilities.enableCreditAppFields ?? creditAppFieldsCoverageToggle;
        const enableCorrectPhoneHandling = useSelector(featureToggleSelectors.isCorrectPhoneHandlingEnabled);
        const creditConsentToggle = useSelector(featureToggleSelectors.isCreditAppConsentEnabled);
        const isPostalCodeDigitsOnlyValidationEnabled = useSelector(featureToggleSelectors.isPostalCodeDigitsOnlyValidationEnabled);
        const isCreditConsentEnabled = capabilities.enableCreditConsent ?? creditConsentToggle;
        // SPD gate — render the section when enabled and the dealer is authorised. A CCID is normally
        // required, but a record being created doesn't have one yet and its fields still need entering.
        const commonConsumerId = initialValues?.commonConsumerId;
        const spdCheckError = useSelector(getSpdCheckError(commonConsumerId));
        const showSpd = Boolean(capabilities.enableSpd && (commonConsumerId || isOperationDraft) && !spdCheckError);
        // Contract rules are skipped while an operation is open; they return once the save lands.
        const isContractContext = Boolean(capabilities.contractValidation) && !isOperationDraft && !isAnyOperationInProgress;
        const activeFlow = isContractContext ? 'default+contract' : 'default';

        // Normalise suffix so the SelectInput finds the correct option regardless of API casing
        const seedValues = useMemo(() => {
            if (!initialValues) return {};
            const seed: Record<string, unknown> = { ...initialValues, suffix: transformSuffix(initialValues.suffix) };
            if (enableCorrectPhoneHandling) {
                seed.cellPhone = resolveCellPhone({
                    cellPhone: initialValues.cellPhone,
                    homePhone: initialValues.homePhone,
                    workPhone: initialValues.workPhone,
                    phone: initialValues.phone
                });
            }
            return seed;
        }, [initialValues, enableCorrectPhoneHandling]);

        const { engine, formValues, formErrors, hasErrors } = useFormValidationWithState(
            { initialConfig: customerValidationConfig, initialValues: seedValues },
            { channel: 'inline' }
        );

        // Keep the active flow in sync when capabilities change (e.g. contracting context toggles mid-session)
        useLayoutEffect(() => {
            engine.setFlow(activeFlow);
        }, [engine, activeFlow]);

        // Keep validation engine ctx in sync so config rules that depend on ctx work correctly:
        // - customerRole: for role-specific rules
        // - customerType: drives Business-specific title length validation
        // - otherEmail: duplicate buyer/co-buyer email check
        // - dealMode: skips Required rules when OptionalDesking is active
        useEffect(() => {
            engine.ctx.entity = 'customer';
            engine.ctx.customerRole = customerRole;
            engine.ctx.customerType = initialValues?.customerType;
            engine.ctx.otherEmail = otherEmail;
            engine.ctx.dealMode = isOptionalDeskingActive ? 'OptionalDesking' : undefined;
            engine.ctx.featureToggles = {
                ...engine.ctx.featureToggles,
                [POSTAL_CODE_DIGITS_ONLY_TOGGLE]: isPostalCodeDigitsOnlyValidationEnabled
            };
        }, [
            engine,
            customerRole,
            initialValues?.customerType,
            otherEmail,
            isOptionalDeskingActive,
            isPostalCodeDigitsOnlyValidationEnabled
        ]);

        // When the other customer's email changes, re-run validation on this form's email
        // field so the duplicate-email error clears (or appears) without requiring the user
        // to re-type their email. engine.ctx.otherEmail is already updated by the effect
        // above (declared first, so it runs first in the same commit).
        useEffect(() => {
            const currentEmail = formValues.email;
            if (currentEmail) {
                engine.setValue('email', String(currentEmail), { validate: true });
            }
        }, [engine, otherEmail]);

        // Baseline for dirty detection — stored in state so that reset() can update it and
        // trigger a re-render which re-evaluates isDirty via the onChange effect below.
        const [baselineValues, setBaselineValues] = useState<Record<string, unknown>>(seedValues);

        // SPD values live here, distinct from the engine's formValues
        const [spdValues, setSpdValuesState] = useState<CustomerSpdValues>({});
        const [spdBaseline, setSpdBaseline] = useState<CustomerSpdValues | null>(null);

        // Dirty = form values differ from the clean baseline. A business conversion has no field-level diff
        // yet (the buyer form is seeded with the business draft), but it is itself a pending change, so it
        // reads as dirty. Single source so the onChange effect and imperative isDirty() stay in sync.
        const isFormDirty = useCallback(
            () =>
                isBusinessConversionFlow ||
                JSON.stringify(normalizeForDirtyCheck(formValues as Record<string, unknown>)) !==
                    JSON.stringify(normalizeForDirtyCheck(baselineValues)),
            [isBusinessConversionFlow, formValues, baselineValues]
        );

        // The parent swaps `initialValues` on the same form instance rather than remounting, so state
        // seeded from it is re-seeded here. SPD is form-local and goes back to its on-file baseline: one
        // participant's SSN/DOB/DL must never carry onto another. Presence itself is left alone — that's
        // Redux state per CCID, so it reappears for whoever we swapped back to.
        const seededSourceRef = useRef(initialValues);
        useEffect(() => {
            if (seededSourceRef.current === initialValues) return;
            seededSourceRef.current = initialValues;
            engine.setValues(seedValues, { mode: 'replace', validate: false });
            setBaselineValues(seedValues);
            setSpdValuesState({});
            setSpdBaseline(null);
        }, [engine, initialValues, seedValues]);

        // Validate upfront for contract flow and when draft is built from search record.
        useEffect(() => {
            if (isContractContext || isDraftFromSearchResult) {
                engine.validateAll();
            }
        }, [engine, isContractContext, isDraftFromSearchResult]);

        const hasContractErrors = useFlowErrorDetector(engine, isContractContext ? 'contract' : null, formErrors);

        const hasBankAccountNumberChanged = spdValues.bankAccountNumber !== (spdBaseline?.bankAccountNumber ?? undefined);
        const isSpdDirty = hasSpdChanged(spdValues, spdBaseline);

        const getValuesWithBankAccountNumber = useCallback((): CustomerFormValues => {
            const values = formValues as CustomerFormValues;
            return {
                ...values,
                birthDate: spdValues.dateOfBirth ? transformBirthDate(spdValues.dateOfBirth) : values.birthDate,
                ...(spdValues.bankAccountNumber !== undefined && {
                    business: { ...values.business, bankAccountNumber: spdValues.bankAccountNumber }
                })
            };
        }, [formValues, spdValues.bankAccountNumber, spdValues.dateOfBirth]);

        // Notify parent after formValues, SPD values, or error state has updated, passing dirty and
        // valid state. formDirty includes SPD and bankAccountNumber so the Apply button enables on
        // either kind of edit — this is distinct from the ref's isDirty(), which useCustomerCardActions
        // uses to gate the participants save/sync call.
        useEffect(() => {
            const formDirty = isFormDirty() || hasBankAccountNumberChanged || isSpdDirty;

            onChange?.(formDirty, !hasErrors, formValues.email as string | undefined, hasContractErrors);
        }, [formValues, hasErrors, onChange, hasBankAccountNumberChanged, isSpdDirty]);

        useImperativeHandle(
            ref,
            () => ({
                validate: async () => {
                    engine.validateAll();
                    return !engine.getHasFormErrors({ flow: activeFlow });
                },
                // Includes bankAccountNumber (folded into business) — this is the full participants payload
                getValues: getValuesWithBankAccountNumber,
                // Participants-only, incl. bankAccountNumber
                isDirty: () => isFormDirty() || hasBankAccountNumberChanged,

                // SSN/DOB/DL only — bankAccountNumber is covered by isDirty()/getValues() instead.
                isSpdDirty: () => isSpdDirty,
                // Plain read of the current SPD fields (SSN, DOB, DL)
                getSpdValues: () => {
                    const { bankAccountNumber: _bankAccountNumber, ...spdOnlyValues } = spdValues;
                    return spdOnlyValues;
                },

                reset: () => {
                    // Clear inline error highlights after a successful save.
                    engine.clearErrors();
                    // Reset dirty-detection baselines so the Apply button returns to disabled — both the
                    // engine-backed baseline and the SPD baseline (SSN/DOB/DL + bankAccountNumber).
                    setBaselineValues({ ...(formValues as Record<string, unknown>) });
                    setSpdBaseline({ ...spdValues });
                }
            }),
            [
                engine,
                formValues,
                activeFlow,
                isFormDirty,
                spdValues,
                getValuesWithBankAccountNumber,
                hasBankAccountNumberChanged,
                isSpdDirty
            ]
        );

        const handleInputChange = useCallback(
            (field: string, value: string) => {
                engine.setValue(field, value, { validate: true });
            },
            [engine]
        );

        const handleNumberInputChange = useCallback(
            (field: string, value: string) => {
                const numericValue = value !== '' ? Number(value) : undefined;
                engine.setValue(field, numericValue, { validate: true });
            },
            [engine]
        );

        const handleSetValues = useCallback(
            (values: Record<string, unknown>) => {
                engine.setValues(values, { mode: 'merge', validate: true });
            },
            [engine]
        );

        const handleCreditConsentChange = useCallback(
            (checked: boolean) => {
                engine.setValue('creditPullConsentGiven', checked, { validate: true });
            },
            [engine]
        );

        const handleClearBillingAddress = useCallback(() => {
            engine.setValue('billingAddress', undefined, { validate: true });
        }, [engine]);

        const handleInitBillingAddress = useCallback(() => {
            engine.setValues(
                { billingAddress: { streetAddress: '', streetAddress2: '', city: '', state: '', zip: '' } },
                { mode: 'merge', validate: false }
            );
        }, [engine]);

        const setSpdValue = useCallback(
            (field: string, value: string) => {
                setSpdValuesState((prev) => ({ ...prev, [field]: value }));
                (engine.ctx.values as Record<string, unknown>)[field] = value;
                engine.validatePath(field, value);
            },
            [engine]
        );

        const setSpdValues = useCallback(
            (fetched: Partial<CustomerSpdValues>) => {
                setSpdValuesState((edited) => mergeNonEmptyFields(fetched, edited));
                setSpdBaseline((prev) => ({ ...prev, ...fetched }));
                // SPD fields never reach engine.ctx.values on their own (PII is deliberately kept out of
                // the seed) — a value can be on file long before the user reveals it, so a contract-flow
                // rule (e.g. dateOfBirth's 100-year check) would never see it unless we feed it in here.
                if (isContractContext) {
                    Object.entries(fetched).forEach(([field, value]) => {
                        (engine.ctx.values as Record<string, unknown>)[field] = value;
                        engine.validatePath(field, value);
                    });
                }
            },
            [engine, capabilities.contractValidation]
        );

        const handleInitPreviousAddress = useCallback(() => {
            engine.setValues(
                {
                    previousAddress: { streetAddress: '', streetAddress2: '', city: '', state: '', zip: '', county: '' },
                    yearsAtPreviousAddress: undefined,
                    monthsAtPreviousAddress: undefined
                },
                { mode: 'merge', validate: false }
            );
        }, [engine]);

        const handleClearPreviousAddress = useCallback(() => {
            engine.setValues(
                {
                    previousAddress: undefined,
                    yearsAtPreviousAddress: undefined,
                    monthsAtPreviousAddress: undefined
                },
                { mode: 'merge', validate: true }
            );
        }, [engine]);

        const handleInitPreviousEmployment = useCallback(() => {
            engine.setValues(
                {
                    previousEmployment: {
                        status: '',
                        employerName: '',
                        occupation: '',
                        workPhone: '',
                        totalYearsEmployed: undefined,
                        totalMonthsEmployed: undefined
                    }
                },
                { mode: 'merge', validate: false }
            );
        }, [engine]);

        const handleClearPreviousEmployment = useCallback(() => {
            engine.setValues({ previousEmployment: undefined }, { mode: 'merge', validate: true });
        }, [engine]);

        const handleInitAdditionalIncome = useCallback(() => {
            engine.setValues(
                {
                    financialInfo: {
                        ...formValues.financialInfo,
                        otherIncomeSource: '',
                        otherIncome: undefined
                    }
                },
                { mode: 'merge', validate: false }
            );
        }, [engine, formValues.financialInfo]);

        const handleClearAdditionalIncome = useCallback(() => {
            engine.setValues(
                {
                    financialInfo: {
                        ...formValues.financialInfo,
                        otherIncomeSource: undefined,
                        otherIncome: undefined
                    }
                },
                { mode: 'merge', validate: true }
            );
        }, [engine, formValues.financialInfo]);

        const handlePhoneInputChange = useCallback(
            (field: PhoneFieldKey, e: InterstateOnChangeEvent<string>) => {
                engine.setValue(field, e.target.value, { validate: false });
                const isValid = e.isValid as boolean;
                const labels: Record<PhoneFieldKey, string> = {
                    phone: 'Cell Phone',
                    cellPhone: 'Cell Phone',
                    homePhone: 'Home Phone',
                    workPhone: 'Work Phone',
                    'business.bankContactPhoneNumber': 'Bank Phone'
                };
                engine.setError(field, isValid, isValid ? '' : `${labels[field]} is invalid`);
            },
            [engine]
        );

        return (
            <CustomerFormContext.Provider
                value={{
                    formValues: formValues as CustomerFormValues,
                    formErrors,
                    customerRole,
                    handleInputChange,
                    handleNumberInputChange,
                    handleSetValues,
                    handleClearBillingAddress,
                    handleInitBillingAddress,
                    handleInitPreviousAddress,
                    handleClearPreviousAddress,
                    handleInitPreviousEmployment,
                    handleClearPreviousEmployment,
                    handleInitAdditionalIncome,
                    handleClearAdditionalIncome,
                    handlePhoneInputChange,
                    handleCreditConsentChange,
                    spdValues,
                    spdBaseline,
                    setSpdValue,
                    setSpdValues,
                    onAnalyticsEvent,
                    isLocked,
                    capabilities,
                    viewMode,
                    isOptionalDeskingActive,
                    isBuyerBusiness,
                    isCurrentCustomerBusiness,
                    isCreditAppFieldsCoverageEnabled,
                    enableCorrectPhoneHandling,
                    hasBusinessData,
                    isOperationDraft,
                    personalInfoHeaderAction,
                    onRequestOperation,
                    hideCheckLoader
                }}
            >
                <div data-testid={`customer-form-${customerRole}`}>
                    {isBusinessBuyer && <BusinessNameField />}
                    <PersonalInfoFields />
                    <PhoneNumberFields />
                    <EmailFields />
                    <CustomerAddresses />
                    {/* Keyed on the participant so we re-check presence whoever the form now points at. */}
                    {showSpd && <CustomerSpd key={(formValues.commonConsumerId as string) ?? 'new'} />}
                    {viewMode !== 'summary' && isCreditAppFieldsCoverageEnabled && !isCurrentCustomerBusiness && (
                        <HousingAndEmploymentInfo />
                    )}
                    {isBusinessBuyer && <BusinessInformation />}
                    {viewMode !== 'summary' && isCreditConsentEnabled && <CreditConsentSection />}
                </div>
            </CustomerFormContext.Provider>
        );
    }
);

CustomerForm.displayName = 'CustomerForm';

export type { CustomerFormProps };
export default CustomerForm;
